import React, { useEffect, useState, useRef } from 'react';
import BakeryCircleItem from "./ui/BakeryCircleItem/BakeryCircleItem";
import "./BakeryProductsStyles.scss"
import ButtonHint from "../../components/Buttons/buttonHint/ButtonHint";
import { useBakeryGroups } from "./lib/hook/useBakeryGroups";
import { useBakeryCircleGrams } from "./lib/hook/useBakeryCircleGrams";
import { UseBakeryTasks } from "./lib/hook/useBakeryTasks";
import BakeryTasksItem from "./ui/BakeryTasksItem/BakeryTasksItem";
import BakeryAddProduct from "./ui/BakeryAddProduct/BakeryAddProduct";
import DataUndefaind from "../DataUndifaend/DataUndefaind";
import BakerySearchAndAddProduct from "./ui/BakerySearchAndAddProduct/BakerySearchAndAddProduct";
import { useBakeryUsers } from "./lib/hook/useBakeryUsers";
import CustomSearch4 from "../../components/CustomSearch2/CustomSearch4";
import CustomAlert from "../../components/CustomAlert/CustomAlert";

const BakeryProducts = () => {
    const { getGroups, groups } = useBakeryGroups();
    const { getCircle, circles } = useBakeryCircleGrams();
    const { getTasks, tasks } = UseBakeryTasks();
    const { loading, getUsers, users } = useBakeryUsers();

    const [selectedGroupId, setSelectedGroupId] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchValue, setSearchValue] = useState('');
    const [modalState, setModalState] = useState({
        isOpen: false,
        type: null,
        product: null
    });
    const [rowsCount, setRowsCount] = useState(3);

    // Состояния для CustomAlert
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [alertTitle, setAlertTitle] = useState('');
    const [alertMessage, setAlertMessage] = useState('');
    const [alertStatusCod, setAlertStatusCod] = useState('');

    // Получаем USER_GUID из localStorage
    const userGuidFromStorage = localStorage.getItem('USER_GUID');

    useEffect(() => {
        const updateRowsCount = () => {
            const width = window.innerWidth;
            if (width >= 1025) {
                setRowsCount(3);
            } else {
                setRowsCount(2);
            }
        };

        updateRowsCount();
        window.addEventListener('resize', updateRowsCount);

        return () => {
            window.removeEventListener('resize', updateRowsCount);
        };
    }, []);

    useEffect(() => {
        getGroups();
        getCircle();
        getUsers();
    }, []);

    useEffect(() => {
        if (groups && groups.length > 0 && !selectedGroupId) {
            const firstGroupId = groups[0].id;
            setSelectedGroupId(firstGroupId);
            getTasks(firstGroupId);
        }
    }, [groups, selectedGroupId, getTasks]);

    const handleGroupClick = (groupId) => {
        if (selectedGroupId === groupId) return;
        setSelectedGroupId(groupId);
        getTasks(groupId);
    };

    const handleUserSelect = (user) => {

        let userGuid = user?.guid || user?.id || user?.originalData?.guid;

        if (!userGuid && user?.originalData) {
            userGuid = user.originalData.guid || user.originalData.id;
        }

        setSelectedUser(userGuid);
        setSearchValue(user?.name || user?.originalData?.full_name || '');
    };

    const clearSelectedUser = () => {
        setSelectedUser(null);
        setSearchValue('');
    };

    // Функция для получения активного GUID пользователя
    const getActiveUserGuid = () => {
        // Если есть выбранный пользователь - используем его
        if (selectedUser) {
            return selectedUser;
        }
        // Если список пользователей пустой или его нет - берем из localStorage
        if ((!users || users.length === 0) && userGuidFromStorage) {
            return userGuidFromStorage;
        }
        return null;
    };

    const openModal = (type, product = null) => {
        setModalState({
            isOpen: true,
            type: type,
            product: product
        });
    };

    const closeModal = () => {
        setModalState({
            isOpen: false,
            type: null,
            product: null
        });
        if (selectedGroupId) {
            getTasks(selectedGroupId);
        }
    };

    const handleProductAdded = () => {
        if (selectedGroupId) {
            getTasks(selectedGroupId);
        }
        clearSelectedUser();
    };

    const showUserRequiredAlert = () => {
        setAlertTitle('Внимание');
        setAlertMessage('Чтобы выполнить действие, сначала выберите пользователя');
        setAlertStatusCod('');
        setIsAlertOpen(true);
    };

    const handleCloseAlert = () => {
        setIsAlertOpen(false);
    };

    const handleOpenAddProductModal = (product) => {
        const activeUserGuid = getActiveUserGuid();

        // Если нет активного пользователя и список пользователей НЕ пустой - показываем алерт
        // Если список пользователей пустой или его нет - алерт не показываем, но и запрос не отправляем
        if (!activeUserGuid) {
            // Если есть список пользователей (не пустой) - показываем алерт
            if (users && users.length > 0) {
                showUserRequiredAlert();
            }
            return;
        }

        let goodsId = product?.goods?.id || product?.id || product?.goods_id;

        const productWithId = {
            ...product,
            goods_id: goodsId,
            goods: product.goods ? {
                ...product.goods,
                id: product.goods.id || goodsId
            } : { id: goodsId, name: product.name }
        };

        openModal('addProduct', productWithId);
    };

    const handleOpenSearchModal = () => {
        const activeUserGuid = getActiveUserGuid();

        if (!activeUserGuid) {
            if (users && users.length > 0) {
                showUserRequiredAlert();
            }
            return;
        }
        openModal('searchAndAdd');
    };

    const getGridColumns = () => {
        if (!tasks || tasks.length === 0) return [];
        const columns = [];
        for (let i = 0; i < tasks.length; i += rowsCount) {
            columns.push(tasks.slice(i, i + rowsCount));
        }
        return columns;
    };

    const gridColumns = getGridColumns();

    const renderModalContent = () => {
        switch (modalState.type) {
            case 'addProduct':
                return <BakeryAddProduct
                    product={modalState.product}
                    onSuccess={handleProductAdded}
                    onClose={closeModal}
                    selectedUserGuid={getActiveUserGuid()}
                />;
            case 'searchAndAdd':
                return <BakerySearchAndAddProduct
                    onOpenAddProductModal={handleOpenAddProductModal}
                    selectedUserGuid={getActiveUserGuid()}
                    onSuccess={handleProductAdded}
                />;
            default:
                return null;
        }
    };

    // Показываем селект только если есть пользователи в списке и данные загружены
    const showUserSelect = loading && users && users.length > 0;

    // Кнопка активна, если есть активный GUID пользователя
    const isButtonActive = !!getActiveUserGuid();

    return (
        <div className="bakery_products">
            <CustomAlert
                title={alertTitle}
                message={alertMessage}
                statusCod={alertStatusCod}
                isOpenAlert={isAlertOpen}
                onClose={handleCloseAlert}
            />

            <div className="bakery_products--top_list">
                {circles?.length > 0 ? (
                    circles.map((item, index) => (
                        <BakeryCircleItem key={index} data={item}/>
                    ))
                ) : (
                    <div className="none_cikle">Циклограмм нет</div>
                )}
            </div>

            <div className="bakery_products--buttons_list">
                <div className="buttons_group">
                    {groups?.map((group) => (
                        <ButtonHint
                            key={group.id}
                            id={group.id}
                            className={`button_group ${selectedGroupId !== group.id ? 'disable_button' : ''}`}
                            text={group.name}
                            funck={() => handleGroupClick(group.id)}
                        />
                    ))}
                </div>
                <div className={`select_add_group`}>
                    {showUserSelect && (
                        <CustomSearch4
                            name={"ФИО пользователя"}
                            data={users}
                            onSelect={handleUserSelect}
                            searchable={true}
                            value={searchValue}
                            initialValue={searchValue}
                            className={"bakery_select_users"}
                        />
                    )}
                    <ButtonHint
                        className="button_add_product"
                        text="Добавить товар"
                        funck={handleOpenSearchModal}
                        disabled={!isButtonActive}
                    />
                </div>
            </div>

            <div className="bakery_products--bottom_grid_wrapper">
                {gridColumns.length === 0 && (<DataUndefaind text={"Задач нет"}/>)}

                <div className="bakery_products--bottom_grid">
                    {gridColumns.map((column, colIndex) => (
                        <React.Fragment key={colIndex}>
                            {column.map((item, rowIndex) => (
                                <BakeryTasksItem
                                    key={item.guid || rowIndex}
                                    data={item}
                                    onAddProduct={handleOpenAddProductModal}
                                    selectedUserGuid={getActiveUserGuid()}
                                    onSuccess={() => {
                                        if (selectedGroupId) {
                                            getTasks(selectedGroupId);
                                        }
                                    }}
                                />
                            ))}
                            {column.length < rowsCount && [...Array(rowsCount - column.length)].map((_, i) => (
                                <div key={`empty_${colIndex}_${i}`} style={{ visibility: 'hidden', height: 0 }} />
                            ))}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {modalState.isOpen && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close" onClick={closeModal}>×</button>
                        {renderModalContent()}
                    </div>
                </div>
            )}
        </div>
    );
};

export default BakeryProducts;